import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
  TextField,
  Box,
  Typography,
  Tooltip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { Can } from "src/helpers/Auth";

interface StageApprovalModalProps {
  open: boolean;
  initialStages: string[];
  onApprove: (stages: string[]) => void;
  onCancel: () => void;
  isApproving?: boolean;
  /** Singular noun for a stage, e.g. "discipline" (from config). */
  stageNounSingular?: string;
  /** Plural noun for stages, e.g. "disciplines" (from config). */
  stageNounPlural?: string;
}

const capitalize = (value: string): string =>
  value ? value.charAt(0).toUpperCase() + value.slice(1) : value;

export const StageApprovalModal: React.FC<StageApprovalModalProps> = ({
  open,
  initialStages,
  onApprove,
  onCancel,
  isApproving,
  stageNounSingular = "discipline",
  stageNounPlural = "disciplines",
}) => {
  const [stages, setStages] = useState<string[]>(initialStages);
  const [newStageName, setNewStageName] = useState("");

  useEffect(() => {
    if (open) {
      setStages(initialStages);
      setNewStageName("");
    }
  }, [open, initialStages]);

  const handleAddStage = () => {
    const trimmed = newStageName.trim();
    if (!trimmed) return;
    setStages((prev) => [...prev, trimmed]);
    setNewStageName("");
  };

  const handleRemoveStage = (index: number) => {
    setStages((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>{`Review & Approve ${capitalize(stageNounPlural)}`}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {`Review the identified ${stageNounPlural} below. You can add or remove ${stageNounPlural}. A stage will be created for each ${stageNounSingular} with the same input artifacts as the review report.`}
        </Typography>

        <List>
          {stages.map((stage, index) => (
            <ListItem
              key={`${stage}-${index}`}
              secondaryAction={
                <IconButton
                  edge="end"
                  aria-label={`Remove ${stageNounSingular}`}
                  onClick={() => handleRemoveStage(index)}
                >
                  <DeleteIcon />
                </IconButton>
              }
            >
              <ListItemText primary={stage} />
            </ListItem>
          ))}
        </List>

        <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
          <TextField
            fullWidth
            size="small"
            label={`Add ${stageNounSingular}`}
            value={newStageName}
            onChange={(e) => setNewStageName(e.target.value)}
            data-attr="proposal-gen:input:add-stage"
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleAddStage();
              }
            }}
          />
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAddStage}
            data-attr="proposal-gen:btn:add-stage"
          >
            Add
          </Button>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onCancel}
          disabled={isApproving}
          data-attr="proposal-gen:btn:cancel-stage-approval"
        >
          Cancel
        </Button>
        <Can
          perform="artifact:write"
          yes={() => (
            <Button
              variant="contained"
              onClick={() => onApprove(stages)}
              disabled={stages.length === 0 || isApproving}
              data-attr="proposal-gen:btn:approve-stages"
            >
              {isApproving ? "Adding..." : "Approve & Create Stages"}
            </Button>
          )}
          no={() => (
            <Tooltip title="User doesn't have permissions artifact:write">
              <span>
                <Button
                  variant="contained"
                  disabled
                  data-attr="proposal-gen:btn:approve-stages"
                >
                  Approve & Create Stages
                </Button>
              </span>
            </Tooltip>
          )}
        />
      </DialogActions>
    </Dialog>
  );
};
